import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PlacementOutcome } from '@shared/placement'
import type { ExerciseContent, ExerciseResponse } from '@shared/schemas/exercise'
import { api } from './api'
import { catalogKeys } from './catalog'

export interface PlacementItem {
  id: string
  unitSlug: string
  prompt: string
  difficulty: number
  content: ExerciseContent
}

export interface PlacementSummary extends PlacementOutcome {
  attemptId: string
  unitsApplied: string[]
  lessonsSkipped: number
  wordsSeeded: number
}

export const placementKeys = { latest: ['placement', 'latest'] as const }

export function useLatestPlacement() {
  return useQuery({
    queryKey: placementKeys.latest,
    queryFn: () =>
      api<{ latest: { id: string; estimated_level: string | null } | null }>(
        '/placement/latest',
      ).then((r) => r.latest),
    staleTime: 5 * 60_000,
  })
}

export function useStartPlacement() {
  return useMutation({
    mutationFn: () =>
      api<{ attemptId: string; items: PlacementItem[]; unitOrder: string[] }>(
        '/placement/start',
        { method: 'POST' },
      ),
  })
}

export function useAnswerPlacement() {
  return useMutation({
    mutationFn: (body: { attemptId: string; itemId: string; response: ExerciseResponse }) =>
      api<{ correct: boolean; stopHere: boolean }>('/placement/answer', {
        method: 'POST',
        body,
      }),
  })
}

export function useFinishPlacement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { attemptId: string }) =>
      api<PlacementSummary>('/placement/finish', { method: 'POST', body }),
    onSuccess: async () => {
      // Placement rewrites lesson progress and her level, so the course map
      // and profile must both be refetched rather than patched.
      await queryClient.invalidateQueries({ queryKey: catalogKeys.courses })
      await queryClient.invalidateQueries({ queryKey: ['courses'] })
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      await queryClient.invalidateQueries({ queryKey: placementKeys.latest })
    },
  })
}
